import { useStaticCallback } from '@sparemin/blockhead';
import { useEffect } from 'react';

export type UsePassthroughEventHandlersProps = {
  onCanPlay: () => void;
  onCanPlayThrough: () => void;
  videoRef: React.RefObject<HTMLVideoElement>;
};

/**
 * Wires up video listeners using addEventListener/removeEventListener so that
 * parent components can handle events using callback props
 *
 * Certain callbacks, like timeupdate, are handled by other hooks because they are
 * more than just simple passthrough listeners
 */
export default function usePassthroughEventHandlers({
  onCanPlay,
  onCanPlayThrough,
  videoRef,
}: UsePassthroughEventHandlersProps) {
  const staticOnCanPlay = useStaticCallback(onCanPlay);
  const staticOnCanPlayThrough = useStaticCallback(onCanPlayThrough);

  useEffect(() => {
    if (videoRef.current) {
      const video = videoRef.current;

      video.addEventListener('canplaythrough', staticOnCanPlayThrough);
      video.addEventListener('canplay', staticOnCanPlay);

      return () => {
        video.removeEventListener('canplaythrough', staticOnCanPlayThrough);
        video.removeEventListener('canplay', staticOnCanPlay);
      };
    }

    return undefined;
  }, [staticOnCanPlay, staticOnCanPlayThrough, videoRef]);
}
